"use client";

import { FIELD_CLASS } from "@/components/ui/field";
import { useActionState, useId } from "react";
import { PASSWORD_MIN_LENGTH } from "@ackra/core";
import { Button } from "@/components/ui/button";
import { InviteMessage } from "./InviteMessage";

/**
 * One form for every auth screen. The server action returns `{ error }` for a
 * failure, and the message is the generic one the flow chose; this component
 * adds nothing to it. `recoveryCodes` is set once, by enrolment, and is shown
 * exactly once with the way on.
 */
export interface AuthState {
  readonly error: string | null;
  /** A one-time message the form shows after success, e.g. an invite link that is displayed only now. */
  readonly notice?: string;
  readonly recoveryCodes?: readonly string[];
  /**
   * TA-1324 T7. An invite's ready-to-paste email, in pieces: the form draws the subject and the body as text a person
   * can select, with a button that copies the body. `notice` carries the same words as one string.
   */
  readonly compose?: InviteCompose;
}

export interface InviteCompose {
  readonly email: string;
  readonly link: string;
  readonly subject: string;
  readonly body: string;
}

export interface AuthField {
  readonly name: string;
  readonly label: string;
  readonly type: "text" | "email" | "password" | "select";
  readonly options?: readonly { readonly value: string; readonly label: string }[];
  readonly autoComplete: string;
  readonly inputMode?: "numeric" | "text";
  readonly hint?: string;
}

const INITIAL: AuthState = { error: null };

/**
 * TA-1370 A13: what the browser can check before the round trip, read from the field's own description. A new
 * password carries the server's minimum (PASSWORD_MIN_LENGTH); a current one does not, so an older password
 * still signs in. The enrolment code (one-time-code on the numeric keypad) is six digits; spaces are allowed
 * anywhere, as verifyTotp allows them (it trims and drops spaces), so a pasted "123 456" is not refused here
 * and accepted there. No maxLength for the same reason. The sign-in code is
 * typed as text because it also takes a recovery code, so it gets no six-digit rule.
 */
function browserChecks(f: AuthField): { minLength?: number; maxLength?: number; pattern?: string } {
  if (f.type === "password" && f.autoComplete === "new-password") return { minLength: PASSWORD_MIN_LENGTH };
  if (f.autoComplete === "one-time-code" && f.inputMode === "numeric") return { pattern: "\\s*(?:[0-9] *){6}\\s*" };
  return {};
}

export function AuthForm({
  action,
  fields,
  submitLabel,
  hidden,
  continueHref,
  submitNamedBy,
}: {
  action: (prev: AuthState, formData: FormData) => Promise<AuthState>;
  fields: readonly AuthField[];
  submitLabel: string;
  hidden?: Readonly<Record<string, string>>;
  continueHref?: string;
  /**
   * TA-1370 S-01: the id of the element that shows whose submit this is, as a member's name on a row of
   * Security's people. The button is then named by its own label followed by that element's text ("Reset link
   * Maya Cohen"), so the visible label starts the name (WCAG 2.5.3) and the person's name is read from the
   * element that shows it, never copied into an attribute.
   */
  submitNamedBy?: string;
}) {
  const [state, formAction, pending] = useActionState(action, INITIAL);
  // TA-1370 A13: the refusal's id, unique per form (Security draws one form per member), so a field can point
  // at it. The refusal answers the submission as a whole, so it marks every field the form shows.
  const errorId = useId();
  const submitId = useId();
  const refused = state.error ? { "aria-invalid": true, "aria-describedby": errorId } : {};

  if (state.recoveryCodes) {
    return (
      <div data-testid="recovery-codes" className="flex flex-col gap-3">
        <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
          Keep these ten recovery codes somewhere safe. Each works once if you lose your phone. They are shown only now.
        </p>
        <ul className="grid grid-cols-2 gap-2 font-[family-name:var(--font-mono)] text-[length:var(--text-sm)]">
          {state.recoveryCodes.map((c) => (
            <li key={c} className="rounded-[var(--radius-md)] border border-[var(--color-border)] px-2 py-1" data-user-content>
              {c}
            </li>
          ))}
        </ul>
        <a
          href={continueHref ?? "/"}
          data-testid="recovery-continue"
          className="inline-flex items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-brand-600)] min-h-9 px-3.5 py-2 text-[length:var(--text-sm)] font-semibold text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
        >
          I have saved them, continue
        </a>
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col gap-4" data-testid="auth-form">
      {hidden
        ? Object.entries(hidden).map(([k, v]) => <input key={k} type="hidden" name={k} value={v} />)
        : null}
      {fields.map((f) => (
        <label key={f.name} className="flex flex-col gap-1.5 text-[length:var(--text-xs)] font-medium text-[var(--color-ink-muted)]">
          {f.label}
          {f.type === "select" ? (
            <select
              name={f.name}
              data-testid={`field-${f.name}`}
              className={`${FIELD_CLASS} font-normal`}
              {...refused}
            >
              {(f.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              name={f.name}
              type={f.type}
              autoComplete={f.autoComplete}
              inputMode={f.inputMode}
              required
              {...browserChecks(f)}
              {...refused}
              data-testid={`field-${f.name}`}
              className={`${FIELD_CLASS} font-normal`}
            />
          )}
          {f.hint ? <span className="font-normal text-[var(--color-ink-subtle)]">{f.hint}</span> : null}
        </label>
      ))}
      {state.error ? (
        <p id={errorId} role="alert" data-testid="auth-error" className="text-[length:var(--text-sm)] text-[var(--color-risk)]">
          {state.error}
        </p>
      ) : null}
      {state.compose ? (
        <InviteMessage compose={state.compose} />
      ) : state.notice ? (
        <p role="status" data-testid="auth-notice" data-user-content className="break-all text-[length:var(--text-sm)] text-[var(--color-ink)]">
          {state.notice}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        disabled={pending}
        data-testid="auth-submit"
        className="py-2"
        id={submitId}
        aria-labelledby={pending || !submitNamedBy ? undefined : `${submitId} ${submitNamedBy}`}
      >
        {pending ? "Working" : submitLabel}
      </Button>
    </form>
  );
}
